import { fetchWithFallback } from "../OrgUsageAnalytics/api";

// Module-level cache so navigating into an org's detail page and back doesn't
// re-fetch the (expensive) overview from Firestore. The CFO dashboard's data
// changes slowly — loan stages move on the order of hours, not seconds — so
// a 5-min TTL feels right while still keeping the Refresh button meaningful.
//
// Keys are per-endpoint + per-env + per-query so different periods or search
// terms never shadow each other. Passing `refresh: true` in the options
// bypasses the cache (the dashboard's Refresh button does this) and also
// pings the backend's `?refresh=true` so its own cache is bypassed in the
// same shot.
//
// Note: this is intentionally a plain Map, not React Query, sessionStorage,
// or anything heavier. The lifetime of this cache is the lifetime of the
// JS module (i.e. the SPA tab), which is exactly what we want for a CFO
// who's navigating within the dashboard.
const CACHE_TTL_MS = 5 * 60 * 1000;
const cache = new Map();

function buildKey(endpoint, env, params) {
  const normalized = Object.entries(params || {})
    .filter(([, v]) => v !== undefined && v !== null && v !== "")
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([k, v]) => `${k}=${v}`)
    .join("&");
  return `${endpoint}|${env}|${normalized}`;
}

function getCached(key) {
  const entry = cache.get(key);
  if (!entry) return null;
  if (Date.now() - entry.cachedAt > CACHE_TTL_MS) {
    cache.delete(key);
    return null;
  }
  return entry.payload;
}

function setCached(key, payload) {
  // Keep the cache bounded. Realistically we hold a handful of entries
  // (one per period × env × org), but cap it so a runaway loop can't grow
  // it without limit.
  if (cache.size >= 64) {
    const oldestKey = cache.keys().next().value;
    if (oldestKey !== undefined) cache.delete(oldestKey);
  }
  cache.set(key, { cachedAt: Date.now(), payload });
}

/**
 * Clear all cached billing-usage responses. Primarily useful when the
 * env or auth context changes underneath us.
 */
export function clearBillingUsageCache() {
  cache.clear();
}

export async function fetchBillingUsageOverview(params, env, options = {}) {
  const { refresh = false } = options;
  const key = buildKey("overview", env, params);

  if (!refresh) {
    const cached = getCached(key);
    if (cached) return cached;
  }

  // Pass through ?refresh=true to the backend so its own in-memory cache
  // is bypassed in lockstep with ours. Without this, a user could hit
  // Refresh and still receive backend-cached data.
  const queryParams = refresh ? { ...params, refresh: "true" } : params;

  const payload = await fetchWithFallback("/billing-usage/overview", queryParams, {
    env,
    timeoutMs: 120000,
  });

  setCached(key, payload);
  return payload;
}

/**
 * Set / clear the manual billing override for a single loan.
 *
 * @param {string} organizationId
 * @param {string} loanId
 * @param {{ isBillable: boolean | null, reason?: string }} payload
 *   - isBillable=true|false sets the override; isBillable=null clears it.
 * @param {string} env
 *
 * After a successful write we blow away the whole client cache: the loan's
 * billable state + charge changed, which ripples into the org's detail
 * summary AND its row in the overview totals, so any cached copy is stale.
 */
export async function setLoanBillingOverride(
  organizationId,
  loanId,
  payload,
  env,
) {
  const result = await fetchWithFallback(
    `/billing-usage/${encodeURIComponent(organizationId)}/loans/${encodeURIComponent(
      loanId,
    )}/billing-override`,
    {},
    { env, method: "POST", body: payload, timeoutMs: 30000 },
  );
  clearBillingUsageCache();
  return result;
}

export async function fetchBillingUsageDetail(
  organizationId,
  params,
  env,
  options = {},
) {
  const { refresh = false } = options;
  const key = buildKey(`detail:${organizationId}`, env, params);

  if (!refresh) {
    const cached = getCached(key);
    if (cached) return cached;
  }

  const queryParams = refresh ? { ...params, refresh: "true" } : params;

  const payload = await fetchWithFallback(
    `/billing-usage/${encodeURIComponent(organizationId)}`,
    queryParams,
    { env, timeoutMs: 120000 },
  );

  setCached(key, payload);
  return payload;
}
